import { useRef, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { message } from "antd";
import CustomInputField from "../../../../CustomHTMLElements/CustomInputField";
import { getData } from "../../../../../newApis/dotNetApiMethods";
import styles from "./BusinessIdentityVerification.module.scss";
import HeaderTitle from "../../Components/HeaderTitle";

const schema = yup.object().shape({
  bvn: yup
    .string()
    .required("Please enter BVN Number")
    .min(11, "BVN must be exactly 11 digits")
    .max(11, "BVN must be exactly 11 digits")
    .matches(/^\d+$/, "BVN must contain only numbers"),
});

type FormData = {
  bvn: string;
};

interface BusinessIdentityVerificationProps {
  onContinue: (data: {
    bvn: string;
    firstname?: string;
    lastname?: string;
    dob?: string;
    gender?: string;
  }) => void;
  defaultValue?: string;
}

const BusinessIdentityVerification: React.FC<
  BusinessIdentityVerificationProps
> = ({ onContinue, defaultValue = "" }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const { register, handleSubmit, errors, watch } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      bvn: defaultValue,
    },
  });

  const bvn = watch("bvn");

  const onSubmit = async (formData: FormData) => {
    setIsVerifying(true);

    try {
      const endpoint = `/customer_verification/kyc_bank_corporate?IdentificationNumber=${formData.bvn}&CountryCode=NG`;
      const response = await getData(endpoint);

      if (response?.data) {
        const {
          data: {
            data: { first_name, last_name, formatted_dob, gender },
          },
        } = response;

        message.success("BVN verified successfully!");

        onContinue({
          bvn: formData.bvn,
          firstname: first_name,
          lastname: last_name,
          dob: formatted_dob ?? "",
          gender,
        });
      } else {
        const errorMsg = "Failed to verify BVN. Please try again.";
        message.error(errorMsg);
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to verify BVN. Please try again.";

      message.error(errorMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleProceedClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <div className={styles.verificationContainer}>
      <div className={styles.content}>
        <HeaderTitle>Enter your BVN to proceed</HeaderTitle>
        <p className={styles.subtitle}>Kindly enter the information below</p>

        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <div className="row mt-4">
            <div className="col-12">
              <CustomInputField
                type="text"
                name="bvn"
                reference={register}
                label="BVN"
                placeholder="Enter your BVN"
                errors={errors?.bvn}
                maxLength={11}
              />
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              onClick={handleProceedClick}
              className="btn advancly-btn btn-lg btn-block"
              disabled={!bvn || isVerifying}
            >
              {isVerifying ? "Verifying..." : "Proceed"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessIdentityVerification;
